import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';
import { Buffer } from 'buffer';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
// import Select from '@mui/material/Select';
// import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';

import Filter from './filter';
import Validator, { useValidate, checkValidity } from '../validator';

import SystemService from '../../data/system-service';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    certBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
        color: theme.palette.text.primary,
    },
    codemirrorBox: {
        display: 'block',
        width: '100%',
    },
}));

const Snmp = forwardRef((props, ref) => {
    const classes = useStyles();
    const [, setSystemConstants] = useState({
        facilities: [],
        severities: [],
        timezones: [],
    });
    const filterRef = useRef(null);
    const [original, setOriginal] = useState(_cloneDeep(props.data));
    const [data, setData] = useState(_cloneDeep(props.data));
    const [comm, setComm] = useState('');
    const [changes, setChanges] = useState({});
    const [init, setInit] = useState(true);
    const dataForm = useValidate({});

    useImperativeHandle(ref, () => ({
        getData: () => {
            const f = filterRef.current.getFilter();
            return {
                ...data,
                filterconf: f,
            };
        },
    }));

    useEffect(() => {
        SystemService.getConstants().then((res) => {
            setSystemConstants(res);
        });
    }, []);

    useEffect(() => {
        const d = { ...props.data };
        if (d.prune_type === '') {
            d.prune_type = 'inactive';
        }
        // for now force only health alerts
        if (d.event_types) {
            d.event_types = ['health_alert'];
        }

        if (init) {
            setOriginal(_cloneDeep(d));
            setData(_cloneDeep(d));
            setInit(false);
        }
    }, [init, props]);

    useEffect(() => {
        setChanges(!_isEqual(data, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (props.onUpdateForm) {
            props.onUpdateForm(dataForm);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataForm]);

    useEffect(() => {
        if (props.data && props.data.snmp) {
            const d = { ...props.data.snmp };
            if (d.community && d.community !== '') {
                const dComm = atob(d.community);
                setComm(dComm);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleHost = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.dest.host = e.target.value;
        setData(d);
    };

    const handleCommunity = (e, validID, valid) => {
        const d = { ...data };
        setComm(e.target.value);
        // eslint-disable-next-line node/no-deprecated-api
        d.snmp.community = new Buffer(comm).toString('base64');
        setData(d);
    };

    const handlePruneTypeChange = (e) => {
        const d = { ...data };
        d.prune_type = e.target.value;
        setData(d);
    };

    const handlePruneKeysChange = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.prune_keys = e.target.value ? e.target.value.replace(/\s/g, '').split(',') : [];
        setData(d);
    };

    const handleUpdateFilter = (filter) => {
        setData({
            ...data,
            filterconf: filter,
        });
    };

    const handleVerify = (k, i, close) => {
        props.onVerify(k, i, close);
    };

    const handleCancel = (k, i) => {
        props.onCancel(k, i);
    };

    const handleEventTypeHealthAlerts = (e) => {
        const d = { ...data };
        d.event_types = ['health_alert'];
        setData(d);
    };

    const handlePruningKeyReference = (e) => {
        window.open('/support/filter_reference');
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box>
                    <Box display="inline-block" pr={5}>Event Type(s):</Box>
                    <FormControlLabel
                        label="Health Alerts"
                        onChange={(e) => { handleEventTypeHealthAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('health_alert') !== -1} />}
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Trap Target Hostname</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="host"
                            errorText="Must be a valid host"
                            fullWidth
                            value={data.dest.host}
                            onChange={handleHost}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Community String (optional)</FormLabel>
                        <TextField
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            type="password"
                            fullWidth
                            value={comm}
                            onChange={handleCommunity}
                        />
                    </FormControl>
                </Box>
                <Box mt={3} display="flex" justifyContent="space-between">
                    <Box>
                        <Typography>Event Pruning Type:</Typography>
                        <RadioGroup row value={data.prune_type} onChange={handlePruneTypeChange}>
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Inactive"
                                aria-label="inactive"
                                control={<Radio value='inactive' />} />
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Remove"
                                aria-label="remove"
                                control={<Radio value='black' />} />
                            <FormControlLabel
                                disabled={(!props.perms.write || props.policySource === 'mc2')}
                                label="Add"
                                aria-label="add"
                                control={<Radio value='white' />} />
                        </RadioGroup>
                    </Box>
                    <Box>
                        <Button variant="contained" color="primary" onClick={handlePruningKeyReference}>Pruning key reference</Button>
                    </Box>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Event Pruning Keys:</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            allowEmpty
                            valueType="keyPairList"
                            errorText="Must be valid comma separated key pairs ex. bricata:dest_location"
                            fullWidth
                            value={data.prune_keys ? data.prune_keys.join(',') : []}
                            onChange={handlePruneKeysChange}
                        />
                    </FormControl>
                </Box>
            </Grid>
            <Filter
                filter={data.filterconf}
                ref={filterRef}
                onUpdate={handleUpdateFilter}
                onVerify={() => { handleVerify(props.name, props.index, false) }}
                perms={props.perms}
            />
            {(props.perms.write || props.policySource !== 'mc2') && <Grid item xs={12}>
                <Box mt={2}>
                    <Button
                        className={classes.buttonRight}
                        variant="contained"
                        color="primary"
                        aria-label="save"
                        onClick={() => { handleVerify(props.name, props.index, true) }}
                        disabled={!(checkValidity(dataForm.get()))}
                    >Verify</Button>
                    <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={() => { handleCancel(props.name, props.index) }}>Cancel</Button>
                </Box>
            </Grid>}
        </Grid>
    );
});

Snmp.propTypes = {
    data: PropTypes.object.isRequired,
    policySource: PropTypes.string,
    name: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    onVerify: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onUpdateForm: PropTypes.func,
    perms: PropTypes.object,
};

export default Snmp;
